import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft, FileDown, ChevronDown, RefreshCw, CalendarClock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { PlanCreationFlow } from "@/components/career/PlanCreationFlow";
import { PlayerSummaryCard } from "@/components/career/PlayerSummaryCard";
import { VisionTimeline } from "@/components/career/VisionTimeline";
import { ObjectivesOverview } from "@/components/career/ObjectivesOverview";
import { MonthlyProgressChart } from "@/components/career/MonthlyProgressChart";
import { MeetingPrepDialog } from "@/components/career/MeetingPrepDialog";
import { GoalsCard, GoalDialog } from "@/components/career/GoalsCard";
import { MilestoneDialog } from "@/components/career/RoadmapCard";
import { ReadinessCard } from "@/components/career/ReadinessCard";
import { VelocityCard } from "@/components/career/VelocityCard";
import { BlockersCard } from "@/components/career/BlockersCard";
import { OpportunitiesCard } from "@/components/career/OpportunitiesCard";
import { ActionsTab, DocumentsTab, HistoryTab } from "@/components/career/CareerTabsExtra";
import { cn } from "@/lib/utils";
import { translateFr as tfr } from "@/i18n";

const TABS = [
  { id: "overview", label: tfr("Vue d'ensemble") },
  { id: "objectifs", label: tfr("Objectifs") },
  { id: "actions", label: tfr("Actions") },
  { id: "analyse", label: tfr("Analyse") },
  { id: "documents", label: "Documents" },
  { id: "historique", label: "Historique" },
];

function ExportMenu({ playerId }) {
  const [exporting, setExporting] = useState(false);

  const doExport = async (audience) => {
    setExporting(true);
    try {
      const r = await api.post(`/players/${playerId}/career-plan/export`, { audience, lang: localStorage.getItem("athlynx_lang") === "en" ? "en" : "fr" });
      const dl = await api.get(`/players/${playerId}/documents/${r.data.document_id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([dl.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = r.data.filename || "plan-carriere.pdf"; a.click();
      URL.revokeObjectURL(url);
      toast.success(`${tfr("PDF généré")} (V${r.data.version}) ${tfr("et ajouté aux documents de l'athlète")}`);
    } catch (e) {
      if (e?.response?.status !== 402) toast.error(tfr("Impossible d'exporter le plan en PDF"));
    } finally { setExporting(false); }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" disabled={exporting} className="h-9 gap-1.5" data-testid="career-export-button" aria-busy={exporting}>
          <FileDown className="h-4 w-4" aria-hidden="true" />
          {exporting ? "Export…" : "Exporter en PDF"}
          <ChevronDown className="h-3.5 w-3.5 opacity-60" aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => doExport("internal")} data-testid="career-export-internal">
          {tfr("Version interne (complète)")}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => doExport("external")} data-testid="career-export-external">
          {tfr("Version partageable (sans données sensibles)")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function CareerPlan() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = TABS.some((t) => t.id === searchParams.get("tab")) ? searchParams.get("tab") : "overview";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [meetingOpen, setMeetingOpen] = useState(false);
  const [goalOpen, setGoalOpen] = useState(false);
  const [milestoneOpen, setMilestoneOpen] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(false);
    try {
      const r = await api.get(`/players/${id}/career-plan/overview`);
      setData(r.data);
    } catch (e) {
      console.error("career-plan overview failed", e);
      if (e?.response?.status === 404) { navigate("/pipeline"); return; }
      setError(true);
    } finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);
  const refresh = useCallback(() => load(true), [load]);

  const setTab = (t) => {
    const next = new URLSearchParams(searchParams);
    if (t === "overview") next.delete("tab"); else next.set("tab", t);
    setSearchParams(next);
  };

  const onAction = (type) => {
    if (type === "goal") setGoalOpen(true);
    else if (type === "milestone") setMilestoneOpen(true);
    else if (type === "meeting") setMeetingOpen(true);
    else if (type === "report") navigate(`/players/${id}/career-plan/builder`);
  };

  if (loading) {
    return (
      <div className="px-4 md:px-8 max-w-7xl mx-auto py-8 space-y-5" data-testid="career-plan-loading">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-28 rounded-2xl" />
        <Skeleton className="h-9 w-96" />
        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,2.2fr)_minmax(0,1fr)] gap-5">
          <div className="space-y-5"><Skeleton className="h-72 rounded-2xl" /><Skeleton className="h-56 rounded-2xl" /><Skeleton className="h-64 rounded-2xl" /></div>
          <div className="space-y-5"><Skeleton className="h-72 rounded-2xl" /><Skeleton className="h-48 rounded-2xl" /><Skeleton className="h-40 rounded-2xl" /></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-4 md:px-8 max-w-6xl mx-auto py-16 text-center" data-testid="career-plan-error">
        <p className="text-sm text-muted-foreground">{tfr("Impossible de charger le plan de carrière.")}</p>
        <Button variant="outline" onClick={() => load()} className="mt-4 gap-2" data-testid="career-plan-retry">
          <RefreshCw className="h-4 w-4" /> Réessayer
        </Button>
      </div>
    );
  }

  if (!data) return null;
  const { player, plan } = data;

  return (
    <div className="pb-16" data-testid="career-plan-page">
      <div className="px-4 md:px-8 max-w-7xl mx-auto py-6 space-y-5">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-1.5 text-xs text-muted-foreground" aria-label="Fil d'Ariane">
          <button onClick={() => navigate(`/players/${id}`)} data-testid="career-plan-back"
            className="rounded-full p-1.5 hover:bg-secondary transition-colors -ml-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-athlynx/40"
            aria-label={tfr("Retour au profil de l'athlète")}>
            <ChevronLeft className="h-4 w-4" />
          </button>
          <Link to="/pipeline" className="hover:text-foreground transition-colors">Joueurs</Link>
          <span aria-hidden="true">›</span>
          <Link to={`/players/${id}`} className="hover:text-foreground transition-colors">{player.name}</Link>
          <span aria-hidden="true">›</span>
          <span className="text-foreground font-medium">Career Development</span>
        </nav>

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl md:text-3xl font-semibold tracking-tight" data-testid="career-plan-title">
              Career Development Plan
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Le plan stratégique de la carrière de {player.name}
            </p>
          </div>
          {plan && (
            <div className="flex items-center gap-2 shrink-0">
              <ExportMenu playerId={id} />
              <Button className="h-9 gap-1.5 bg-athlynx hover:bg-athlynx/90 text-white"
                onClick={() => setMeetingOpen(true)} data-testid="career-meeting-btn">
                <CalendarClock className="h-4 w-4" aria-hidden="true" /> Préparer une réunion
              </Button>
            </div>
          )}
        </div>

        {!plan ? (
          <PlanCreationFlow playerId={id} onCreated={refresh} />
        ) : (
          <>
            <PlayerSummaryCard player={player} contract={data.contract} plan={plan} onAction={onAction} onChanged={refresh} />

            {/* Tabs */}
            <div className="flex items-center gap-1 border-b border-border overflow-x-auto" data-testid="career-tabs" role="tablist">
              {TABS.map((t) => (
                <button key={t.id} onClick={() => setTab(t.id)} role="tab" aria-selected={tab === t.id}
                  data-testid={`career-tab-${t.id}`}
                  className={cn("relative px-3.5 py-2.5 text-sm font-medium whitespace-nowrap transition-colors",
                    tab === t.id ? "text-foreground" : "text-muted-foreground hover:text-foreground")}>
                  {t.label}
                  {tab === t.id && <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-athlynx" />}
                </button>
              ))}
            </div>

            {tab === "overview" && (
              <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,2.2fr)_minmax(0,1fr)] gap-5" data-testid="career-overview-grid">
                <div className="space-y-5 min-w-0">
                  <VisionTimeline plan={plan} milestones={data.milestones} playerId={id} onChanged={refresh} />
                  <ObjectivesOverview goals={data.goals} onOpenAll={() => setTab("objectifs")} />
                  <MonthlyProgressChart history={data.history} />
                </div>
                <div className="space-y-5 min-w-0">
                  <ReadinessCard readiness={data.readiness} />
                  <BlockersCard blockers={data.blockers} playerId={id} onChanged={refresh} />
                  <OpportunitiesCard opportunities={data.opportunities} suggestions={data.market_suggestions} playerId={id} onChanged={refresh} />
                  <VelocityCard velocity={data.velocity} />
                </div>
              </div>
            )}

            {tab === "objectifs" && (
              <div className="max-w-3xl" data-testid="career-tab-objectifs-content">
                <GoalsCard goals={data.goals} playerId={id} onChanged={refresh} />
              </div>
            )}

            {tab === "actions" && <div className="max-w-3xl"><ActionsTab playerId={id} /></div>}

            {tab === "analyse" && (
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-5" data-testid="career-tab-analyse-content">
                <div className="space-y-5"><ReadinessCard readiness={data.readiness} /><VelocityCard velocity={data.velocity} /></div>
                <MonthlyProgressChart history={data.history} />
              </div>
            )}

            {tab === "documents" && <div className="max-w-3xl"><DocumentsTab playerId={id} /></div>}

            {tab === "historique" && <div className="max-w-3xl"><HistoryTab playerId={id} /></div>}
          </>
        )}
      </div>

      <MeetingPrepDialog open={meetingOpen} onOpenChange={setMeetingOpen} playerId={id} />
      <GoalDialog open={goalOpen} onOpenChange={setGoalOpen} playerId={id} onChanged={refresh} />
      <MilestoneDialog open={milestoneOpen} onOpenChange={setMilestoneOpen} playerId={id} onChanged={refresh} />
    </div>
  );
}
